// Phase 17 · Ask-your-data chatbot page
import { useEffect, useState } from "react";
import { Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AskPage() {
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const [asking, setAsking] = useState(false);
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    try {
      const [h, t] = await Promise.all([
        api.get("/ai/ask/history?limit=20"),
        api.get("/ai/ask/templates"),
      ]);
      setHistory(h.data.items || []);
      setTemplates(t.data.templates || []);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!question.trim()) return;
    setAsking(true);
    try {
      const r = await api.post("/ai/ask", { question: question.trim() });
      setHistory([{ question: question.trim(), answer_text: r.data.answer_text,
                     intent: r.data.template_id ? { template_id: r.data.template_id } : null,
                     row_count: r.data.data?.rows?.length || 0,
                     _data: r.data.data,
                     created_at: new Date().toISOString(),
                     id: Math.random().toString(36).slice(2) }, ...history]);
      setQuestion("");
    } catch (e) {
      if (e?.response?.status === 429) toast.error("Rate limit — try again next hour");
      else toast.error(e?.response?.data?.detail || "Ask failed");
    } finally { setAsking(false); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="ask-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[#E5197F]" /> Ask your data
        </h1>
        <p className="text-xs text-neutral-500 mt-1">
          Ask about sales, coverage, dormant outlets, top/bottom performers. Answers stay within your visibility scope.
        </p>
      </div>

      <div className="flex gap-2">
        <input value={question} onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => e.key === "Enter" && send()}
                placeholder="e.g. 'top 5 SEs by sales this month' or 'coverage by zone today'"
                data-testid="ask-input"
                className="flex-1 h-12 px-4 rounded-lg border border-neutral-300 text-sm" />
        <button onClick={send} disabled={asking || !question.trim()}
                 data-testid="ask-submit"
                 className="h-12 px-5 rounded-lg bg-[#E5197F] text-white font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Ask
        </button>
      </div>

      <div className="text-[11px] text-neutral-500">
        Suggested: {templates.slice(0, 5).map(t => (
          <button key={t.id} onClick={() => setQuestion(t.description)}
                   className="ml-2 underline hover:text-[#E5197F]"
                   data-testid={`template-${t.id}`}>
            {t.id.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {history.map((h, i) => (
          <div key={h.id || i} className="bg-white rounded-xl border p-4 space-y-2" data-testid={`qa-${i}`}>
            <div className="text-xs text-neutral-500">Q · {new Date(h.created_at).toLocaleTimeString()}</div>
            <div className="text-sm font-medium">{h.question}</div>
            <div className="border-t pt-2 text-sm text-neutral-800" data-testid={`qa-answer-${i}`}>{h.answer_text}</div>
            {h._data?.rows?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-t mt-2">
                  <thead className="text-neutral-500">
                    <tr>{h._data.columns.map(c => <th key={c} className="text-left py-1 pr-3">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {h._data.rows.slice(0, 10).map((r, idx) => (
                      <tr key={idx} className="border-t">
                        {h._data.columns.map(c => <td key={c} className="py-1 pr-3">{typeof r[c] === "number" ? r[c].toLocaleString("en-IN") : r[c]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {h.intent?.template_id && <div className="text-[10px] text-neutral-400">via `{h.intent.template_id}` template · {h.row_count} rows</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
